import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Nav, Tab, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { usePeriodBuffer } from '../context/PeriodBufferContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';

const DashboardHome = () => {
  const { user } = useAuth();
  const { updatePeriodBuffer, deletePeriodBuffer } = usePeriodBuffer();
  const { isDarkMode, setDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [apiResponse, setApiResponse] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Inventory Period state
  const [inventoryPeriods, setInventoryPeriods] = useState([]);
  const [periodsLoading, setPeriodsLoading] = useState(false);
  const [selectedPeriodId, setSelectedPeriodId] = useState('');
  const [periodValue, setPeriodValue] = useState('');
  const [periodDescription, setPeriodDescription] = useState('');
  const [existingMeta, setExistingMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [periodError, setPeriodError] = useState('');
  const [periodSuccess, setPeriodSuccess] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  // Fetch inventory periods on mount
  useEffect(() => {
    const fetchInventoryPeriods = async () => {
      setPeriodsLoading(true);
      try {
        const response = await axios.get('/inventory-periods', {
          withCredentials: true
        });
        const periods = response.data?.data || response.data || [];
        // Exclude "Daily" inventory option
        const filtered = periods.filter(p => {
          const name = (p.inventoryPeriodName || p.inventoryPeriodDesc || '').toLowerCase();
          return name !== 'daily';
        });
        setInventoryPeriods(filtered);
      } catch (error) {
        console.error('Error loading inventory periods:', error);
        setPeriodError('Failed to load inventory periods');
      } finally {
        setPeriodsLoading(false);
      }
    };

    if (user?.userGroup === 0 || user?.userGroup === 1) {
      fetchInventoryPeriods();
    }
  }, [user]);

  // Check if meta exists when period is selected
  useEffect(() => {
    const checkMetaExists = async () => {
      if (!selectedPeriodId) {
        setExistingMeta(null);
        setPeriodValue('');
        setPeriodDescription('');
        return;
      }

      setMetaLoading(true);
      try {
        const response = await axios.get(
          `/inventory-periods-meta/by-period-and-name/${selectedPeriodId}/periodbuffer`,
          { withCredentials: true }
        );

        console.log('Meta response:', response.data);

        // Handle nested data structure
        const metaData = response.data?.data || response.data;

        if (metaData) {
          setExistingMeta(metaData);
          setPeriodValue(metaData.value || '');
          setPeriodDescription(metaData.description || '');
        } else {
          setExistingMeta(null);
          setPeriodValue('');
          setPeriodDescription('');
        }
      } catch (error) {
        if (error.response?.status === 404) {
          // Meta doesn't exist, which is fine
          setExistingMeta(null);
          setPeriodValue('');
          setPeriodDescription('');
        } else {
          console.error('Error checking meta:', error);
          setPeriodError('Failed to check existing meta');
        }
      } finally {
        setMetaLoading(false);
      }
    };

    checkMetaExists();
  }, [selectedPeriodId]);

  const handleSyncToDo = async () => {
    setIsLoading(true);
    setApiResponse('');

    try {
      const response = await axios.post('/azure-services/trigger-todo-integrate', {}, {
        withCredentials: true
      });

      setApiResponse(JSON.stringify(response.data, null, 2));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      setApiResponse(`Error: ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePeriodMeta = async () => {
    setPeriodError('');
    setPeriodSuccess('');

    if (!selectedPeriodId) {
      setPeriodError('Please select an inventory period');
      return;
    }

    const numValue = Number(periodValue);
    if (!periodValue || isNaN(numValue) || numValue < 1 || numValue > 24) {
      setPeriodError('Value must be a number between 1 and 24');
      return;
    }

    setSavingMeta(true);
    try {
      const payload = {
        inventoryPeriodsId: selectedPeriodId,
        name: 'periodbuffer',
        value: numValue,
        description: periodDescription || undefined
      };

      if (existingMeta) {
        // Update existing meta
        await axios.put(`/inventory-periods-meta/${existingMeta.id}`, payload, {
          withCredentials: true
        });
        setPeriodSuccess('Inventory period meta updated successfully!');
      } else {
        // Create new meta
        await axios.post('/inventory-periods-meta', payload, {
          withCredentials: true
        });
        setPeriodSuccess('Inventory period meta created successfully!');
      }

      // Refresh the meta data
      const response = await axios.get(
        `/inventory-periods-meta/by-period-and-name/${selectedPeriodId}/periodbuffer`,
        { withCredentials: true }
      );
      const metaData = response.data?.data || response.data;
      setExistingMeta(metaData);

      // Update the global context
      updatePeriodBuffer(selectedPeriodId, numValue);
    } catch (error) {
      console.error('Error saving meta:', error);
      setPeriodError(error.response?.data?.message || 'Failed to save inventory period meta');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDeletePeriodMeta = async () => {
    if (!existingMeta) return;

    if (!window.confirm('Are you sure you want to delete this inventory period meta?')) {
      return;
    }

    setSavingMeta(true);
    setPeriodError('');
    setPeriodSuccess('');

    try {
      await axios.delete(`/inventory-periods-meta/${existingMeta.id}`, {
        withCredentials: true
      });
      setPeriodSuccess('Inventory period meta deleted successfully!');
      setExistingMeta(null);
      setPeriodValue('');
      setPeriodDescription('');

      // Update the global context
      deletePeriodBuffer(selectedPeriodId);
    } catch (error) {
      console.error('Error deleting meta:', error);
      setPeriodError(error.response?.data?.message || 'Failed to delete inventory period meta');
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div className="content-container">
      <h3>Welcome to Your Dashboard</h3>

      <Container fluid className="mt-4">
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Row>
            <Col>
              <Nav variant="tabs" className="mb-3">
                <Nav.Item>
                  <Nav.Link eventKey="overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="analytics">Analytics</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="configuration">Configuration</Nav.Link>
                </Nav.Item>
              </Nav>

              <Tab.Content>
                <Tab.Pane eventKey="overview">
                  <div className="p-3">
                    <h5>Overview</h5>
                    <p>This is the overview tab where you can see a summary of your dashboard data.</p>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h6 className="card-title">Quick Stats</h6>
                            <p className="card-text">View your key metrics at a glance.</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h6 className="card-title">Recent Activity</h6>
                            <p className="card-text">See what's been happening recently.</p>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card">
                          <div className="card-body">
                            <h6 className="card-title">System Status</h6>
                            <p className="card-text">Check the current system status.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="analytics">
                  <div className="p-3">
                    <h5>Analytics</h5>
                    <p>This is the analytics tab where you can view detailed analytics and charts.</p>
                    <div className="row">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-body">
                            <h6 className="card-title">Performance Metrics</h6>
                            <p className="card-text">Analytics charts and performance data will be displayed here.</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab.Pane>

                <Tab.Pane eventKey="configuration">
                  <div className="p-3">
                    <Form.Check
                      type="checkbox"
                      id="dark-mode-toggle"
                      label="Dark Mode"
                      checked={isDarkMode}
                      onChange={(e) => setDarkMode(e.target.checked)}
                      className="mb-3"
                    />

                    {(user?.userGroup === 0 || user?.userGroup === 1) && (
                      <>
                        <div className="d-flex justify-content-end mb-3">
                          <Button
                            variant="primary"
                            onClick={handleSyncToDo}
                            disabled={isLoading}
                          >
                            {isLoading ? 'Syncing...' : 'Sync ToDo App'}
                          </Button>
                        </div>

                        {apiResponse && (
                          <div className="mb-4">
                            <Form.Label>API Response:</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={10}
                              value={apiResponse}
                              readOnly
                              className="font-monospace"
                              style={{ fontSize: '0.9rem' }}
                            />
                          </div>
                        )}

                        {/* Inventory Period Section */}
                        <hr className="my-4" />
                        <h5 className="mb-3">Inventory Period</h5>

                        {periodError && <Alert variant="danger">{periodError}</Alert>}
                        {periodSuccess && <Alert variant="success">{periodSuccess}</Alert>}

                        <Form.Group className="mb-3">
                          <Form.Label>Select Inventory Period</Form.Label>
                          <Form.Select
                            value={selectedPeriodId}
                            onChange={(e) => setSelectedPeriodId(e.target.value)}
                            disabled={periodsLoading || metaLoading || savingMeta}
                          >
                            <option value="">
                              {periodsLoading ? 'Loading periods...' : 'Select an inventory period'}
                            </option>
                            {inventoryPeriods.map((period) => (
                              <option key={period.id} value={period.id}>
                                {period.inventoryPeriodDesc || period.inventoryPeriodName}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        {metaLoading && (
                          <div className="text-center mb-3">
                            <Spinner animation="border" size="sm" />
                            <span className="ms-2">Checking existing meta...</span>
                          </div>
                        )}

                        {selectedPeriodId && !metaLoading && (
                          <>
                            <Form.Group className="mb-3">
                              <Form.Label>Value (1-24) *</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                max="24"
                                value={periodValue}
                                onChange={(e) => setPeriodValue(e.target.value)}
                                disabled={savingMeta}
                                placeholder="Enter a value between 1 and 24"
                              />
                            </Form.Group>

                            <Form.Group className="mb-3">
                              <Form.Label>Description (optional)</Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={3}
                                value={periodDescription}
                                onChange={(e) => setPeriodDescription(e.target.value)}
                                disabled={savingMeta}
                                placeholder="Enter a description"
                              />
                            </Form.Group>

                            <div className="d-flex gap-2">
                              <Button
                                variant="primary"
                                onClick={handleSavePeriodMeta}
                                disabled={savingMeta}
                              >
                                {savingMeta ? (
                                  <>
                                    <Spinner as="span" size="sm" animation="border" className="me-2" />
                                    {existingMeta ? 'Updating...' : 'Creating...'}
                                  </>
                                ) : (
                                  existingMeta ? 'Update' : 'Create'
                                )}
                              </Button>

                              {existingMeta && (
                                <Button
                                  variant="danger"
                                  onClick={handleDeletePeriodMeta}
                                  disabled={savingMeta}
                                >
                                  {savingMeta ? (
                                    <>
                                      <Spinner as="span" size="sm" animation="border" className="me-2" />
                                      Deleting...
                                    </>
                                  ) : (
                                    'Delete'
                                  )}
                                </Button>
                              )}
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>
    </div>
  );
};

export default DashboardHome;